import { ADMIN_ENDPOINTS } from './endpoints.ts';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../api/oauth/helpers.ts';

export interface RbacRole {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface RbacFeature {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface RoleFeaturesResponse {
  role_key: string;
  feature_keys: string[];
  unrestricted: boolean;
}

export async function fetchRbacRoles(): Promise<RbacRole[]> {
  const data = await apiGet(ADMIN_ENDPOINTS.RBAC_ROLES);
  return (data?.roles as RbacRole[]) ?? [];
}

export async function fetchRbacFeatures(): Promise<RbacFeature[]> {
  const data = await apiGet(ADMIN_ENDPOINTS.RBAC_FEATURES);
  return (data?.features as RbacFeature[]) ?? [];
}

export async function fetchRoleFeatures(roleKey: string): Promise<RoleFeaturesResponse> {
  const data = await apiGet(ADMIN_ENDPOINTS.RBAC_ROLE_FEATURES(roleKey));
  return {
    role_key: data?.role_key ?? roleKey,
    feature_keys: Array.isArray(data?.feature_keys) ? data.feature_keys : [],
    unrestricted: Boolean(data?.unrestricted),
  };
}

export async function setRoleFeatures(roleKey: string, featureKeys: string[]): Promise<RoleFeaturesResponse> {
  const data = await apiPut(ADMIN_ENDPOINTS.RBAC_ROLE_FEATURES(roleKey), {
    feature_keys: featureKeys,
  });
  return {
    role_key: data?.role_key ?? roleKey,
    feature_keys: Array.isArray(data?.feature_keys) ? data.feature_keys : [],
    unrestricted: Boolean(data?.unrestricted),
  };
}

export async function createRbacRole(payload: {
  key: string;
  name: string;
  description?: string;
}): Promise<RbacRole> {
  const data = await apiPost(ADMIN_ENDPOINTS.RBAC_ROLES, payload);
  return data?.role as RbacRole;
}

export async function updateRbacRole(
  roleKey: string,
  payload: { name?: string; description?: string }
): Promise<RbacRole> {
  const data = await apiPatch(ADMIN_ENDPOINTS.RBAC_ROLE_BY_KEY(roleKey), payload);
  return data?.role as RbacRole;
}

export async function createRbacFeature(payload: {
  key: string;
  name: string;
  description?: string;
}): Promise<RbacFeature> {
  const data = await apiPost(ADMIN_ENDPOINTS.RBAC_FEATURES, payload);
  return data?.feature as RbacFeature;
}

export async function updateRbacFeature(
  featureKey: string,
  payload: { name?: string; description?: string }
): Promise<RbacFeature> {
  const data = await apiPatch(ADMIN_ENDPOINTS.RBAC_FEATURE_BY_KEY(featureKey), payload);
  return data?.feature as RbacFeature;
}

export async function deleteRbacFeature(featureKey: string): Promise<void> {
  await apiDelete(ADMIN_ENDPOINTS.RBAC_FEATURE_BY_KEY(featureKey));
}
